import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Divider,
  Chip,
  Button,
  Box,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useAuthFetch } from '../hooks/authFetch';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import CheckIcon from '@mui/icons-material/Check';
import BugReportIcon from '@mui/icons-material/BugReport';
import WcIcon from '@mui/icons-material/Wc';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import BedIcon from '@mui/icons-material/Bed';
import ChairIcon from '@mui/icons-material/Chair';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import SensorOccupiedIcon from '@mui/icons-material/SensorOccupied';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import AssignDeviceModal from './AssignDeviceModal';
import EventTimeline from './EventTimeline';
import { FixedSizeList as List } from 'react-window';
import type { ListChildComponentProps } from 'react-window';

interface EventSummary {
  start_time: string;
  duration: string;
  item_type: string;
  location: string;
}

interface ExtendedEventSummary extends EventSummary {
  device_id: string;
  device_name: string;
}

interface DeviceDetail {
  device_id: string;
  model: string;
  default_location: string;
  name: string;
  events: EventSummary[];
}

interface SeniorDetailData {
  senior_id: string;
  name: string;
  organization_id: string;
  devices: Record<string, DeviceDetail>;
}

const SeniorDetail: React.FC = () => {
  const { senior_id } = useParams<{ senior_id?: string }>();
  console.log('senior_id:', senior_id);
  const navigate = useNavigate();
  const [detail, setDetail] = useState<SeniorDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDeviceIdx, setSelectedDeviceIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();
  const DEFAULT_RANGE_HOURS = 168; // 7 days
  const LAZY_LOAD_INCREMENT_HOURS = 168;

  const rangePresets = useMemo(
    () => [
      { label: '24h', hours: 24 },
      { label: '48h', hours: 48 },
      { label: '7d', hours: 168 },
      { label: '30d', hours: 720 },
    ],
    []
  );

  const [rangeMode, setRangeMode] = useState<'preset' | 'custom'>('preset');
  const [rangeHours, setRangeHours] = useState<number>(DEFAULT_RANGE_HOURS);
  const [pendingCustomRange, setPendingCustomRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [appliedCustomRange, setAppliedCustomRange] = useState<{ start: Dayjs; end: Dayjs } | null>(null);
  const [appliedCustomRangeTZ, setAppliedCustomRangeTZ] = useState<{ startDate: string; endDate: string; timeZone: string } | null>(null);
  // Timezone state (declared early to be available for handlers below)
  const [selectedTimezone, setSelectedTimezone] = useState('America/New_York');
  const hasAppliedOrgTimezone = useRef(false);

  const clearCustomRange = useCallback(() => {
    setPendingCustomRange([null, null]);
    setAppliedCustomRange(null);
    setAppliedCustomRangeTZ(null);
  }, []);

  const handleRangePresetChange = useCallback((hours: number) => {
    if (rangeMode === 'preset' && rangeHours === hours) return;
    clearCustomRange();
    setRangeMode('preset');
    setRangeHours(hours);
    setSelectedDeviceIdx(0);
  }, [clearCustomRange, rangeMode, rangeHours]);


const handleCustomRangeApply = useCallback(
  (value: [Dayjs | null, Dayjs | null]) => {
    const [rawStart, rawEnd] = value;
    if (!rawStart || !rawEnd) return;
    const normalizedStart = rawStart.startOf('day');
    let normalizedEnd = rawEnd.endOf('day');
    const now = dayjs();
    if (normalizedEnd.isAfter(now)) {
      normalizedEnd = now;
    }
    const hoursNeeded = Math.ceil(now.diff(normalizedStart, 'hour', true));
    const safeHours = Math.max(hoursNeeded, DEFAULT_RANGE_HOURS);
    setRangeMode('custom');
    setRangeHours(safeHours);
    setAppliedCustomRange({ start: normalizedStart, end: normalizedEnd });
    // Also store date-only range in selected timezone for accurate filtering
    const startDateStr = rawStart.format('YYYY-MM-DD');
    const endDateStr = rawEnd.format('YYYY-MM-DD');
    setAppliedCustomRangeTZ({ startDate: startDateStr, endDate: endDateStr, timeZone: selectedTimezone });
    setPendingCustomRange([normalizedStart, normalizedEnd]);
    setSelectedDeviceIdx(0);
  },
  [DEFAULT_RANGE_HOURS, selectedTimezone]
);

const buildDetailUrl = useCallback(
    (overrideHours?: number) => {
      if (!senior_id) return null;
      const base = `${import.meta.env.VITE_SERVER_URL}/api/v1/events/get-detail?senior_id=${senior_id}`;
      const hoursParam = overrideHours ?? rangeHours;
      return `${base}&hours=${Math.max(hoursParam, 1)}`;
    },
    [senior_id, rangeHours]
  );

  const fetchDetail = useCallback(async () => {
    // Backoff guard to avoid rapid retries after failures
    const nowTs = Date.now();
    if (nowTs < nextAllowedFetchTsRef.current) {
      return;
    }
    if (!senior_id) {
      setError('No senior_id provided in URL.');
      setLoading(false);
      return;
    }
    const url = buildDetailUrl();
    if (!url) return;
    // Inflight guard (skip if a request is already in progress)
    const inflightKey = `${senior_id}:${rangeHours}`;
    if (inflightKeyRef.current === inflightKey || inflightKeyRef.current !== null) {
      return;
    }
    inflightKeyRef.current = inflightKey;
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(url);
      if (!res.ok) throw new Error('Failed to fetch senior detail');
      const data = await res.json();
      setDetail(data);
      // Merge events into a lightweight dedupe map for lazy-load scenarios
      try {
        const map = eventsByIdRef.current;
        let minStart: number | null = earliestLoadedStart;
        let maxEnd: number | null = latestLoadedEnd;

        const devices = (data?.devices || {}) as Record<string, DeviceDetail>;
        for (const did of Object.keys(devices)) {
          const dev = devices[did];
          const devName = dev?.name || dev?.model || did;
          for (const e of dev?.events || []) {
            const key = `${did}|${e.start_time}|${e.item_type}`;
            if (!map.has(key)) {
              const startTs = Date.parse(e.start_time || '');
              // Parse duration to compute end time
              const parts = String(e.duration || '').split(':').map(Number);
              let durSec = 0;
              if (parts.length === 3) durSec = parts[0] * 3600 + parts[1] * 60 + parts[2];
              else if (parts.length === 2) durSec = parts[0] * 60 + parts[1];
              else if (parts.length === 1) durSec = parts[0] || 0;
              const endTs = Number.isFinite(startTs) ? startTs + durSec * 1000 : null;

              map.set(key, {
                ...e,
                device_id: did,
                device_name: devName,
                location: did === 'dv50cc67' ? 'bedroom' : dev?.default_location || e.location || 'unknown location',
              } as ExtendedEventSummary);

              if (Number.isFinite(startTs)) {
                minStart = minStart == null ? startTs : Math.min(minStart, startTs);
              }
              if (endTs != null && Number.isFinite(endTs)) {
                maxEnd = maxEnd == null ? endTs : Math.max(maxEnd, endTs);
              }
            }
          }
        }

        if (minStart !== earliestLoadedStart) setEarliestLoadedStart(minStart ?? null);
        if (maxEnd !== latestLoadedEnd) setLatestLoadedEnd(maxEnd ?? null);
        setEventsVersion(v => v + 1);
      } catch {
        // Non-fatal: keep detail but skip dedupe merge on error
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch senior detail');
      // Simple backoff on failure
      nextAllowedFetchTsRef.current = Date.now() + 2000;
    } finally {
      setLoading(false);
      if (loadingOlderRef.current) {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      }
      inflightKeyRef.current = null;
    }
  }, [authFetch, buildDetailUrl, rangeHours, senior_id]);

  // Reset deduped events store when senior changes
  useEffect(() => {
    eventsByIdRef.current = new Map();
    setEarliestLoadedStart(null);
    setLatestLoadedEnd(null);
    setEventsVersion(v => v + 1);
  }, [senior_id]);

  // Assign device modal state
  const handleTimelineNearStart = useCallback(() => {
    if (rangeMode === 'custom') return;
    if (loadingOlderRef.current || loading) return;
    const now = Date.now();
    if (now - lastLazyLoadTsRef.current < 5000) return; // 5s cooldown between lazy-loads
    lastLazyLoadTsRef.current = now;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    setRangeMode('preset');
    setRangeHours(prev => prev + LAZY_LOAD_INCREMENT_HOURS);
  }, [LAZY_LOAD_INCREMENT_HOURS, loading, rangeMode]);

  // Assign device modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);
  const loadingOlderRef = useRef(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const inflightKeyRef = useRef<string | null>(null);
  const nextAllowedFetchTsRef = useRef<number>(0);
  const lastLazyLoadTsRef = useRef<number>(0);

  // Lightweight dedupe store for events
  const eventsByIdRef = useRef<Map<string, ExtendedEventSummary>>(new Map());
  const [eventsVersion, setEventsVersion] = useState(0);
  const [earliestLoadedStart, setEarliestLoadedStart] = useState<number | null>(null);
  const [latestLoadedEnd, setLatestLoadedEnd] = useState<number | null>(null);
  const [selectedTimelineKey, setSelectedTimelineKey] = useState<string | null>(null);

  // Unassign device confirmation state
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [deviceToUnassign, setDeviceToUnassign] = useState<{
    device_id: string;
    device_name: string;
  } | null>(null);
  const [unassignLoading, setUnassignLoading] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);

  // Delete senior confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Debug mode state
  const [debugMode, setDebugMode] = useState(false);

  // Duration filter state (persisted between sessions for convenience)
  const [minDurationFilter, setMinDurationFilter] = useState<number>(() => {
    if (typeof window === 'undefined') return 90;
    const stored = window.localStorage.getItem('minDurationFilterSeconds');
    if (stored === null) return 90;
    const parsed = Number.parseInt(stored, 10);
    return Number.isNaN(parsed) ? 90 : parsed;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        'minDurationFilterSeconds',
        String(minDurationFilter)
      );
    }
  }, [minDurationFilter]);

  useEffect(() => {
    if (!detail || hasAppliedOrgTimezone.current) return;
    const orgId = (detail.organization_id || '').toLowerCase();
    if (orgId === 'eskaton_pilot' || orgId.includes('eskaton')) {
      setSelectedTimezone('America/Los_Angeles');
    }
    hasAppliedOrgTimezone.current = true;
  }, [detail]);

  const durationFilterOptions = useMemo(
    () => [
      { value: 0, label: 'Show all durations' },
      { value: 30, label: '>= 30 seconds' },
      { value: 60, label: '>= 1 minute' },
      { value: 90, label: '>= 1 minute 30 seconds' },
      { value: 120, label: '>= 2 minutes' },
      { value: 180, label: '>= 3 minutes' },
      { value: 300, label: '>= 5 minutes' },
    ],
    []
  );

  const formatThresholdLabel = (seconds: number): string => {
    if (seconds <= 0) return 'all events';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    }
    return `${minutes} min ${remainingSeconds}s`;
  };

  const handleDurationFilterChange = (event: SelectChangeEvent<string>) => {
    const nextValue = Number.parseInt(event.target.value, 10);
    setMinDurationFilter(Number.isNaN(nextValue) ? 0 : nextValue);
  };

  const isFilteringByDuration = !debugMode && minDurationFilter > 0;

  // Edit device modal state
  const [editDeviceModalOpen, setEditDeviceModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<{
    device_id: string;
    name: string;
    model: string;
    default_location: string;
  } | null>(null);
  const [editDeviceLoading, setEditDeviceLoading] = useState(false);
  const [editDeviceError, setEditDeviceError] = useState<string | null>(null);
  const [editDeviceSuccess, setEditDeviceSuccess] = useState<string | null>(
    null
  );

  // Utility function to get icon for activity type
  const getActivityIcon = (itemType: string) => {
    const activityType = itemType.replace(/_/g, ' ').toLowerCase();

    if (activityType.includes('bathroom') || activityType.includes('toilet')) {
      return (
        <WcIcon
          sx={{
            fontSize: '1rem',
            mr: 1,
            color: '#6b7280',
            alignSelf: 'center',
            p: 0.25,
          }}
        />
      );
    }
    if (
      activityType.includes('eating') ||
      activityType.includes('dining') ||
      activityType.includes('meal')
    ) {
      return (
        <RestaurantIcon
          sx={{
            fontSize: '1rem',
            mr: 1,
            color: '#6b7280',
            alignSelf: 'center',
            p: 0.25,
          }}
        />
      );
    }
    if (
      activityType.includes('sleeping') ||
      activityType.includes('bed') ||
      activityType.includes('nap')
    ) {
      return (
        <BedIcon
          sx={{
            fontSize: '1rem',
            mr: 1,
            color: '#6b7280',
            alignSelf: 'center',
            p: 0.25,
          }}
        />
      );
    }
    if (activityType.includes('sitting') || activityType.includes('chair')) {
      return (
        <ChairIcon
          sx={{
            fontSize: '1rem',
            mr: 1,
            color: '#6b7280',
            alignSelf: 'center',
            p: 0.25,
          }}
        />
      );
    }
    if (
      activityType.includes('walking') ||
      activityType.includes('moving') ||
      activityType.includes('movement')
    ) {
      return (
        <DirectionsWalkIcon
          sx={{
            fontSize: '1rem',
            mr: 1,
            color: '#6b7280',
            alignSelf: 'center',
            p: 0.25,
          }}
        />
      );
    }
    if (
      activityType.includes('human detected') ||
      activityType.includes('person detected') ||
      activityType.includes('human') ||
      activityType.includes('detection')
    ) {
      return (
        <SensorOccupiedIcon
          sx={{
            fontSize: '1rem',
            mr: 1,
            color: '#6b7280',
            alignSelf: 'center',
            p: 0.25,
          }}
        />
      );
    }
    if (activityType.includes('room') || activityType.includes('location')) {
      return (
        <HomeIcon
          sx={{
            fontSize: '1rem',
            mr: 1,
            color: '#6b7280',
            alignSelf: 'center',
            p: 0.25,
          }}
        />
      );
    }

    // Default icon for unknown activity types
    return (
      <EventIcon
        sx={{
          fontSize: '1rem',
          mr: 1,
          color: '#6b7280',
          alignSelf: 'center',
          p: 0.25,
        }}
      />
    );
  };

  // Common timezones for the selector
  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'UTC', label: 'UTC' },
  ];

  // Utility function to format date/time in selected timezone
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: selectedTimezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Compact month/day label for loaded range (respects selected timezone)
  const formatShortDateFromMs = (ms: number): string => {
    try {
      const date = new Date(ms);
      return date.toLocaleDateString('en-US', {
        timeZone: selectedTimezone,
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };

  // Date-only (YYYY-MM-DD) in a specific IANA timezone using Intl
  const formatDateYMDInTZ = (ms: number, tz: string): string => {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(ms));
    } catch {
      const d = new Date(ms);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  };

  // Utility function to format duration in a human-readable way
  const formatDuration = (duration: string): string => {
    if (!duration || typeof duration !== 'string') return '';

    const seconds = parseDurationToSeconds(duration);
    if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''}`;

    const totalMinutes = Math.round(seconds / 60); // Round to nearest minute for cleaner display

    if (totalMinutes < 60) {
      return `${totalMinutes} minute${totalMinutes !== 1 ? 's' : ''}`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} and ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
  };

  // Utility function to format event for user-friendly display
  const formatEventDisplay = (event: EventSummary | ExtendedEventSummary) => {
    const duration = formatDuration(event.duration);
    const time = formatDateTime(event.start_time);

    // Use device's default location instead of event location
    let location = 'unknown location';
    let resolvedDeviceId: string | undefined;
    if ('device_id' in event) {
      // For extended events (All Devices view), get device default location
      const extendedEvent = event as ExtendedEventSummary;
      resolvedDeviceId = extendedEvent.device_id;
      const device = devicesMap[extendedEvent.device_id];
      location = device?.default_location || 'unknown location';
    } else if (selectedDeviceKey !== 'ALL_DEVICES') {
      // For single device view, use selected device's default location
      resolvedDeviceId = selectedDeviceKey;
      const device = devicesMap[selectedDeviceKey];
      location = device?.default_location || 'unknown location';
    }

    if (resolvedDeviceId === 'dv50cc67') {
      location = 'bedroom';
    }

    // Clean up item_type for better display
    const activityType = event.item_type.replace(/_/g, ' ').toLowerCase();
    if (activityType === 'bathroom trip') {
      if (resolvedDeviceId === 'dv50cc67') {
        return `${duration} in the ${location}\nthe ${location} @ ${time}`;
      }
      return `${duration} using the bathroom\nthe ${location} @ ${time}`;
    }

    return `${duration} ${activityType}\nthe ${location} @ ${time}`;
  };

  // Utility function to parse duration string and return seconds
  const parseDurationToSeconds = (duration: string): number => {
    if (!duration || typeof duration !== 'string') return 0;

    // Clean the duration string
    const cleanDuration = duration.trim();

    // Handle formats like "1:30", "0:45", "2:15:30", etc.
    const parts = cleanDuration.split(':').map(part => {
      const parsed = parseInt(part, 10);
      return isNaN(parsed) ? 0 : parsed;
    });

    if (parts.length === 1) {
      // Assume seconds only
      return parts[0];
    } else if (parts.length === 2) {
      // mm:ss format
      return parts[0] * 60 + parts[1];
    } else if (parts.length === 3) {
      // hh:mm:ss format
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }

    return 0;
  };


const isEventInActiveRange = useCallback(
  (startTime: string) => {
    if (rangeMode === 'custom' && appliedCustomRangeTZ) {
      const ts = Date.parse(startTime);
      if (!Number.isFinite(ts)) return false;
      // Compare by the event's calendar date in the selected timezone
      const eventDate = formatDateYMDInTZ(ts, appliedCustomRangeTZ.timeZone);
      return (
        eventDate >= appliedCustomRangeTZ.startDate &&
        eventDate <= appliedCustomRangeTZ.endDate
      );
    }
    return true;
  },
  [appliedCustomRangeTZ, rangeMode]
);

// Filter events based on debug mode (show only events longer than configured threshold)
const filterEventsByDuration = <T extends EventSummary>(events: T[]): T[] => {
  let filtered = events;
  if (!debugMode && minDurationFilter > 0) {
    filtered = filtered.filter(
      event => parseDurationToSeconds(event.duration) >= minDurationFilter
    );
  }
  return filtered.filter(event => isEventInActiveRange(event.start_time));
};

  // Kick off data fetch when key inputs change; avoid depending on function identity
  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senior_id, rangeHours]);

  // Assign device handler
  const handleAssignDevice = async (deviceId: string) => {
    setAssignLoading(true);
    setAssignError(null);
    setAssignSuccess(null);
    try {
      const res = await authFetch(
        `${import.meta.env.VITE_SERVER_URL}/api/v1/devices/${encodeURIComponent(deviceId)}/assign?senior_id=${encodeURIComponent(senior_id ?? '')}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (res.ok) {
        setAssignSuccess('Device assigned successfully.');
        await fetchDetail();
      } else {
        const data = await res.json();
        setAssignError(data.error || 'Failed to assign device.');
      }
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Network error');
    }
    setAssignLoading(false);
  };

  // Unassign device handler
  const handleUnassignDevice = async () => {
    if (!deviceToUnassign) return;

    setUnassignLoading(true);
    try {
      const res = await authFetch(
        `${import.meta.env.VITE_SERVER_URL}/api/v1/devices/${encodeURIComponent(deviceToUnassign.device_id)}/unassign?senior_id=${encodeURIComponent(senior_id ?? '')}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (res.ok) {
        // Close dialog and refresh data
        setUnassignDialogOpen(false);
        setDeviceToUnassign(null);
        await fetchDetail();
      } else {
        const data = await res.json();
        console.error('Failed to unassign device:', data);
      }
    } catch (error) {
      console.error('Network error during unassign:', error);
    }
    setUnassignLoading(false);
  };

  // Handle unassign button click
  const handleUnassignClick = (deviceId: string, deviceName: string) => {
    setDeviceToUnassign({ device_id: deviceId, device_name: deviceName });
    setUnassignDialogOpen(true);
  };

  // Delete senior handler
  const handleDeleteSenior = async () => {
    if (!senior_id) return;

    setDeleteLoading(true);
    try {
      const res = await authFetch(
        `${import.meta.env.VITE_SERVER_URL}/api/v1/seniors/${encodeURIComponent(senior_id)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (res.ok) {
        // Navigate back to the seniors list or dashboard
        navigate(-1);
      } else {
        const data = await res.json();
        console.error('Failed to delete senior:', data);
        setError(data.error || 'Failed to delete senior.');
      }
    } catch (error) {
      console.error('Network error during senior deletion:', error);
      setError('Network error during senior deletion');
    }
    setDeleteLoading(false);
    setDeleteDialogOpen(false);
  };

  // Handle edit device button click
  const handleEditDeviceClick = async (deviceId: string) => {
    setEditDeviceError(null);
    setEditDeviceSuccess(null);
    setEditDeviceLoading(true);

    try {
      // Fetch the full device metadata from the API
      const res = await authFetch(
        `${import.meta.env.VITE_SERVER_URL}/api/v1/devices/${encodeURIComponent(deviceId)}`
      );

      if (res.ok) {
        const deviceMetadata = await res.json();
        setEditingDevice({
          device_id: deviceId,
          name: deviceMetadata.nickname || '',
          model: deviceMetadata.model || '',
          default_location: deviceMetadata.default_location || '',
        });
        setEditDeviceModalOpen(true);
      } else {
        const errorData = await res.json();
        setEditDeviceError(
          errorData.error || 'Failed to fetch device metadata.'
        );
        // Fallback to local data if API fails
        const device = detail?.devices?.[deviceId];
        if (device) {
          setEditingDevice({
            device_id: deviceId,
            name: device.name || '',
            model: device.model || '',
            default_location: device.default_location || '',
          });
          setEditDeviceModalOpen(true);
        }
      }
    } catch (error) {
      setEditDeviceError(
        error instanceof Error ? error.message : 'Network error'
      );
      // Fallback to local data if network error
      const device = detail?.devices?.[deviceId];
      if (device) {
        setEditingDevice({
          device_id: deviceId,
          name: device.name || '',
          model: device.model || '',
          default_location: device.default_location || '',
        });
        setEditDeviceModalOpen(true);
      }
    } finally {
      setEditDeviceLoading(false);
    }
  };

  // Handle save device changes
  const handleSaveDeviceChanges = async () => {
    if (!editingDevice) return;

    setEditDeviceLoading(true);
    setEditDeviceError(null);
    setEditDeviceSuccess(null);

    try {
      const res = await authFetch(
        `${import.meta.env.VITE_SERVER_URL}/api/v1/devices/${encodeURIComponent(editingDevice.device_id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nickname: editingDevice.name,
            model: editingDevice.model,
            default_location: editingDevice.default_location,
          }),
        }
      );

      if (res.ok) {
        setEditDeviceSuccess('Device updated successfully.');
        await fetchDetail();

        // Close modal after short delay to show success message
        setTimeout(() => {
          setEditDeviceModalOpen(false);
          setEditingDevice(null);
        }, 1500);
      } else {
        const data = await res.json();
        setEditDeviceError(data.error || 'Failed to update device.');
      }
    } catch (error) {
      setEditDeviceError(
        error instanceof Error ? error.message : 'Network error'
      );
    }
    setEditDeviceLoading(false);
  };

  const devicesMap = detail?.devices || {};
  const deviceIds = Object.keys(devicesMap);

  // Build events arrays from deduped map
  const allEvents: ExtendedEventSummary[] = useMemo(() => {
    const arr = Array.from(eventsByIdRef.current.values());
    // Apply duration + range filter (range handled inside filterEventsByDuration via isEventInActiveRange)
    // filterEventsByDuration expects EventSummary[]; ExtendedEventSummary is compatible
    return filterEventsByDuration(arr);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsVersion, minDurationFilter, rangeMode, appliedCustomRange, debugMode]);

  // Create extended device list with "All Devices" option
  const extendedDeviceList = ['ALL_DEVICES', ...deviceIds];
  const selectedDeviceKey =
    extendedDeviceList[selectedDeviceIdx] || 'ALL_DEVICES';

  // Determine what events to show based on selection
  const selectedDeviceEvents: (EventSummary | ExtendedEventSummary)[] = useMemo(() => {
    if (selectedDeviceKey === 'ALL_DEVICES') {
      return allEvents.slice().sort((a, b) => {
        const ta = Date.parse(a.start_time || '');
        const tb = Date.parse(b.start_time || '');
        if (!isNaN(ta) && !isNaN(tb)) return tb - ta;
        return String(b.start_time || '') > String(a.start_time || '') ? 1 : -1;
      });
    }
    // Filter deduped events by device id
    const perDevice = Array.from(eventsByIdRef.current.values()).filter(
      (e) => e.device_id === selectedDeviceKey
    );
    return filterEventsByDuration(perDevice);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEvents, selectedDeviceKey, eventsVersion, minDurationFilter, rangeMode, appliedCustomRange, debugMode]);

  // Sort events for the table (device-specific)
  const sortedEvents =
    selectedDeviceKey === 'ALL_DEVICES'
      ? (selectedDeviceEvents as ExtendedEventSummary[])
      : (selectedDeviceEvents as EventSummary[]).slice().sort((a, b) => {
          const ta = Date.parse(a.start_time || '');
          const tb = Date.parse(b.start_time || '');
          if (!isNaN(ta) && !isNaN(tb)) return tb - ta;
          return String(b.start_time || '') > String(a.start_time || '') ? 1 : -1;
        });
  // After all hooks and memos, handle simple loading/error fallbacks
  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (error || !detail)
    return (
      <Alert severity='error'>{error || 'Error loading senior detail.'}</Alert>
    );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container
        sx={{
          mt: 4,
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          p: 4,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Button
          variant='outlined'
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: 2,
            borderColor: 'rgba(59, 130, 246, 0.3)',
            color: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
            fontWeight: 500,
            textTransform: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#3b82f6',
              color: 'white',
              borderColor: '#3b82f6',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            },
          }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={selectedTimezone}
              onChange={e => setSelectedTimezone(e.target.value)}
              label='Timezone'
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(59, 130, 246, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                },
              }}
            >
              {timezones.map(tz => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size='small'
            sx={{ minWidth: 200 }}
            disabled={debugMode}
          >
            <InputLabel>Event Duration Filter</InputLabel>
            <Select
              value={String(minDurationFilter)}
              onChange={handleDurationFilterChange}
              label='Event Duration Filter'
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(59, 130, 246, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                },
              }}
            >
              {durationFilterOptions.map(option => (
                <MenuItem key={option.value} value={String(option.value)}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={debugMode}
                onChange={e => setDebugMode(e.target.checked)}
                color='secondary'
                size='small'
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BugReportIcon sx={{ fontSize: '1rem', color: '#6b7280' }} />
                <Typography
                  variant='caption'
                  sx={{ color: '#6b7280', fontWeight: 500 }}
                >
                  Debug
                </Typography>
              </Box>
            }
            sx={{
              mr: 1,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.75rem',
              },
            }}
          />
          {editMode && (
            <Button
              variant='contained'
              color='error'
              onClick={() => setDeleteDialogOpen(true)}
              startIcon={<DeleteIcon />}
              sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                fontWeight: 500,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#dc2626',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                },
              }}
            >
              Delete Senior
            </Button>
          )}
          <Button
            variant={editMode ? 'contained' : 'outlined'}
            onClick={() => setEditMode(!editMode)}
            startIcon={editMode ? <CheckIcon /> : <EditIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              textTransform: 'none',
              transition: 'all 0.2s ease',
              ...(editMode
                ? {
                    backgroundColor: '#10b981',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      backgroundColor: '#059669',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
                    },
                  }
                : {
                    borderColor: 'rgba(107, 114, 128, 0.3)',
                    color: '#374151',
                    backgroundColor: 'rgba(107, 114, 128, 0.05)',
                    '&:hover': {
                      backgroundColor: '#374151',
                      color: 'white',
                      borderColor: '#374151',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(55, 65, 81, 0.3)',
                    },
                  }),
            }}
          >
            {editMode ? 'Done Editing' : 'Edit'}
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PersonIcon
          sx={{
            mr: 1,
            color: '#3b82f6',
            fontSize: '2rem',
            filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))',
          }}
        />
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.025em',
            color: '#111827',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
          }}
        >
          {detail.name}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderRadius: 2,
            px: 2,
            py: 1,
          }}
        >
          <BadgeIcon sx={{ mr: 0.5, color: '#3b82f6', fontSize: '1.1rem' }} />
          <Typography
            variant='subtitle1'
            sx={{
              fontWeight: 500,
              color: '#374151',
            }}
          >
            {senior_id}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderRadius: 2,
            px: 2,
            py: 1,
          }}
        >
          <BusinessIcon
            sx={{ mr: 0.5, color: '#10b981', fontSize: '1.1rem' }}
          />
          <Typography
            variant='subtitle1'
            sx={{
              fontWeight: 500,
              color: '#374151',
            }}
          >
            {detail.organization_id}
          </Typography>
        </Box>
      </Box>
      <Divider
        sx={{
          my: 3,
          opacity: 0.3,
          background:
            'linear-gradient(90deg, transparent, #3b82f6, transparent)',
        }}
      />
      {/* Timeline visualization for ALL devices */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            color: '#111827',
            letterSpacing: '-0.025em',
          }}
        >
          Event Timeline
        </Typography>
        <Typography variant='caption' sx={{ color: '#6b7280' }}>
          ({selectedTimezone})
        </Typography>
        {isFilteringByDuration && (
          <Chip
            label={`Showing events >= ${formatThresholdLabel(minDurationFilter)}`}
            size='small'
            color='secondary'
            variant='outlined'
            sx={{
              fontSize: '0.7rem',
              height: '20px',
              fontWeight: 500,
            }}
          />
        )}
      </Box>
      <EventTimeline events={allEvents} onRequestOlder={handleTimelineNearStart} timeZone={selectedTimezone} selectedKey={selectedTimelineKey || undefined} />
      {loadingOlder && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant='caption' color='text.secondary'>Loading earlier events...</Typography>
        </Box>
      )}
      {earliestLoadedStart != null && latestLoadedEnd != null && (
        <Typography variant='caption' color='text.secondary' sx={{ mb: 2, display: 'block' }}>
          Loaded: {formatShortDateFromMs(earliestLoadedStart)} → {formatShortDateFromMs(latestLoadedEnd)}
        </Typography>
      )}
      {/* Device Selector with Add Icon */}
      <div style={{ marginBottom: 16 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography
            variant='h6'
            gutterBottom
            sx={{
              mr: 1,
              display: 'flex',
              alignItems: 'center',
              fontWeight: 600,
              color: '#111827',
              letterSpacing: '-0.025em',
            }}
          >
            Devices
            {editMode && (
              <IconButton
                size='small'
                color='primary'
                onClick={() => setAssignModalOpen(true)}
                aria-label='assign device'
                sx={{
                  ml: 1,
                  border: '2px solid',
                  borderColor: '#3b82f6',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
                  },
                }}
              >
                <AddIcon fontSize='small' />
              </IconButton>
            )}
          </Typography>
        </Box>
        {/* Add "All Devices" option first */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            marginRight: 1,
            marginBottom: 1,
            position: 'relative',
          }}
        >
          <Chip
            label='All Devices'
            color={selectedDeviceIdx === 0 ? 'primary' : 'default'}
            onClick={() => setSelectedDeviceIdx(0)}
            sx={{
              cursor: 'pointer',
              borderRadius: 2,
              fontWeight: 500,
              transition: 'all 0.2s ease',
              ...(selectedDeviceIdx === 0
                ? {
                    backgroundColor: '#10b981',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      backgroundColor: '#059669',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
                    },
                  }
                : {
                    backgroundColor: 'rgba(107, 114, 128, 0.1)',
                    color: '#374151',
                    '&:hover': {
                      backgroundColor: 'rgba(107, 114, 128, 0.2)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(107, 114, 128, 0.2)',
                    },
                  }),
            }}
          />
        </Box>
        {deviceIds.map((did, idx) => {
          const dev = devicesMap[did] || {};
          const displayName = dev.name
            ? `${dev.name} (${did})`
            : `${dev.model || did}`;
          const isSelected = selectedDeviceIdx === idx + 1; // +1 because "All Devices" is at index 0
          return (
            <Box
              key={did}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                marginRight: 1,
                marginBottom: 1,
                position: 'relative',
              }}
            >
              <Chip
                label={displayName}
                color={isSelected ? 'primary' : 'default'}
                onClick={() => {
                  setSelectedDeviceIdx(idx + 1); // +1 because "All Devices" is at index 0
                }}
                sx={{
                  cursor: 'pointer',
                  paddingRight: editMode ? '64px' : '8px', // Increased padding for both edit and delete icons
                  borderRadius: 2,
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  ...(isSelected
                    ? {
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        '&:hover': {
                          backgroundColor: '#2563eb',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
                        },
                      }
                    : {
                        backgroundColor: 'rgba(107, 114, 128, 0.1)',
                        color: '#374151',
                        '&:hover': {
                          backgroundColor: 'rgba(107, 114, 128, 0.2)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(107, 114, 128, 0.2)',
                        },
                      }),
                }}
              />
              {editMode && (
                <>
                  <IconButton
                    size='small'
                    sx={{
                      position: 'absolute',
                      right: 26, // Positioned to the left of the delete button
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 20,
                      height: 20,
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'primary.light',
                      },
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      handleEditDeviceClick(did);
                    }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    size='small'
                    sx={{
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 20,
                      height: 20,
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'error.main',
                        backgroundColor: 'error.light',
                      },
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      handleUnassignClick(did, displayName);
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </>
              )}
            </Box>
          );
        })}
      </div>

      <AssignDeviceModal
        open={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setAssignError(null);
          setAssignSuccess(null);
        }}
        organizationId={detail.organization_id}
        onAssign={handleAssignDevice}
        assignLoading={assignLoading}
        assignError={assignError}
        assignSuccess={assignSuccess}
        assignedDeviceIds={Object.keys(detail.devices || {})}
      />
      {/* Events Table (simple) */}
      <Paper
        sx={{
          p: 3,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              color: '#111827',
              letterSpacing: '-0.025em',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {selectedDeviceKey === 'ALL_DEVICES'
              ? 'All Device Events'
              : 'Selected Device Events'}
            {isFilteringByDuration && (
              <Chip
                label={`Filtered (>= ${formatThresholdLabel(minDurationFilter)})`}
                size='small'
                color='secondary'
                variant='outlined'
                sx={{
                  fontSize: '0.7rem',
                  height: '20px',
                  fontWeight: 500,
                }}
              />
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <DatePicker
              label="Start"
              value={pendingCustomRange[0]}
              onChange={(value: Dayjs | null) => setPendingCustomRange([value, pendingCustomRange[1]])}
              maxDate={dayjs()}
              slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
            />
            <DatePicker
              label="End"
              value={pendingCustomRange[1]}
              onChange={(value: Dayjs | null) => setPendingCustomRange([pendingCustomRange[0], value])}
              maxDate={dayjs()}
              slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => handleCustomRangeApply(pendingCustomRange)}
              disabled={!pendingCustomRange[0] || !pendingCustomRange[1]}
            >
              Apply
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearCustomRange();
                handleRangePresetChange(DEFAULT_RANGE_HOURS);
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
        {sortedEvents.length === 0 ? (
          <Typography
            color='text.secondary'
            sx={{
              textAlign: 'center',
              py: 4,
              fontStyle: 'italic',
              color: '#6b7280',
            }}
          >
            {selectedDeviceKey === 'ALL_DEVICES'
              ? 'No events found across all devices.'
              : 'No events for the selected device.'}
          </Typography>
        ) : (
          <>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
              {sortedEvents.length.toLocaleString()} events shown
            </Typography>
            <List
              height={Math.min(560, Math.max(240, sortedEvents.length * 110))}
              width={'100%'}
              itemCount={sortedEvents.length}
              itemSize={110}
              overscanCount={6}
            >
              {({ index, style }: ListChildComponentProps) => {
                const event = sortedEvents[index] as EventSummary | ExtendedEventSummary;
                const isExtendedEvent = selectedDeviceKey === 'ALL_DEVICES';
                const extendedEvent = event as ExtendedEventSummary;
                const did = 'device_id' in event ? (event as ExtendedEventSummary).device_id : selectedDeviceKey;
                const displayType = did === 'dv50cc67' ? 'bedroom_presence' : event.item_type;
                const timelineKey = `${did}|${event.start_time}|${displayType}`;
                return (
                  <div style={style} key={isExtendedEvent ? `${extendedEvent.device_id}-${index}` : index}>
                    <Box
                      sx={{
                        background:
                          'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(16, 185, 129, 0.05))',
                        border: '1px solid rgba(59, 130, 246, 0.1)',
                        borderRadius: 3,
                        p: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                          borderColor: 'rgba(59, 130, 246, 0.2)',
                        },
                        mx: 0.5,
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedTimelineKey(timelineKey)}
                    >
                      {isExtendedEvent ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 1,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant='subtitle2'
                              sx={{
                                fontWeight: 600,
                                color: '#111827',
                                whiteSpace: 'pre-line',
                                display: 'flex',
                                alignItems: 'flex-start',
                              }}
                            >
                              {!debugMode && getActivityIcon(event.item_type)}
                              <span>
                                {debugMode
                                  ? `${event.item_type} @ ${event.start_time}`
                                  : formatEventDisplay(event)}
                              </span>
                            </Typography>
                            {!debugMode && (
                              <Typography
                                variant='caption'
                                sx={{
                                  color: '#9ca3af',
                                  fontWeight: 400,
                                  display: 'block',
                                  mt: 0.5,
                                  ml: !debugMode ? '2rem' : 0,
                                }}
                              >
                                {event.start_time} • {event.duration}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={extendedEvent.device_name}
                            size='small'
                            color='primary'
                            variant='outlined'
                            sx={{
                              fontSize: '0.7rem',
                              height: '20px',
                              fontWeight: 500,
                              ml: 2,
                            }}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <Typography
                            variant='subtitle2'
                            sx={{
                              fontWeight: 600,
                              color: '#111827',
                              mb: 1,
                              whiteSpace: 'pre-line',
                              display: 'flex',
                              alignItems: 'flex-start',
                            }}
                          >
                            {!debugMode && getActivityIcon(event.item_type)}
                            <span>
                              {debugMode
                                ? `${event.item_type} @ ${event.start_time}`
                                : formatEventDisplay(event)}
                            </span>
                          </Typography>
                          {!debugMode && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: '#9ca3af',
                                fontWeight: 400,
                                display: 'block',
                                ml: !debugMode ? '2rem' : 0,
                              }}
                            >
                              {event.duration} • {event.start_time}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {debugMode && (
                        <>
                          <Typography variant='body2' sx={{ color: '#6b7280', mb: 0.5 }}>
                            Duration: {event.duration}
                          </Typography>
                          <Typography variant='body2' sx={{ color: '#6b7280' }}>
                            Location:{' '}
                            {(() => {
                              if ('device_id' in event) {
                                const extended = event as ExtendedEventSummary;
                                const device = devicesMap[extended.device_id];
                                return device?.default_location || 'unknown location';
                              } else if (selectedDeviceKey !== 'ALL_DEVICES') {
                                const device = devicesMap[selectedDeviceKey];
                                return device?.default_location || 'unknown location';
                              }
                              return event.location || 'unknown location';
                            })()}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </div>
                );
              }}
            </List>
          </>
        )}
      </Paper>

      {/* Unassign Device Confirmation Dialog */}
      <Dialog
        open={unassignDialogOpen}
        onClose={() => !unassignLoading && setUnassignDialogOpen(false)}
      >
        <DialogTitle>Confirm Device Removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove device{' '}
            <strong>{deviceToUnassign?.device_name}</strong> from this senior?
            This will unassign the device but not delete it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUnassignDialogOpen(false);
              setDeviceToUnassign(null);
            }}
            disabled={unassignLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUnassignDevice}
            color='error'
            variant='contained'
            disabled={unassignLoading}
          >
            {unassignLoading ? 'Removing...' : 'Remove Device'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Senior Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Senior Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete senior{' '}
            <strong>{detail?.name}</strong>? This will archive the senior record
            but preserve all historical data. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSenior}
            color='error'
            variant='contained'
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Senior'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Device Modal */}
      <Dialog
        open={editDeviceModalOpen}
        onClose={() => !editDeviceLoading && setEditDeviceModalOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Edit Device</DialogTitle>
        <DialogContent>
          {editDeviceError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {editDeviceError}
            </Alert>
          )}
          {editDeviceSuccess && (
            <Alert severity='success' sx={{ mb: 2 }}>
              {editDeviceSuccess}
            </Alert>
          )}
          {editDeviceLoading && !editingDevice ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography>Loading device metadata...</Typography>
            </Box>
          ) : (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
            >
              <TextField
                label='Device Name'
                value={editingDevice?.name || ''}
                onChange={e => {
                  if (editingDevice) {
                    setEditingDevice({
                      ...editingDevice,
                      name: e.target.value,
                    });
                  }
                }}
                fullWidth
                disabled={editDeviceLoading}
              />
              <TextField
                label='Model'
                value={editingDevice?.model || ''}
                onChange={e => {
                  if (editingDevice) {
                    setEditingDevice({
                      ...editingDevice,
                      model: e.target.value,
                    });
                  }
                }}
                fullWidth
                disabled={editDeviceLoading}
              />
              <TextField
                label='Default Location'
                value={editingDevice?.default_location || ''}
                onChange={e => {
                  if (editingDevice) {
                    setEditingDevice({
                      ...editingDevice,
                      default_location: e.target.value,
                    });
                  }
                }}
                fullWidth
                disabled={editDeviceLoading}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDeviceModalOpen(false);
              setEditingDevice(null);
              setEditDeviceError(null);
              setEditDeviceSuccess(null);
            }}
            disabled={editDeviceLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveDeviceChanges}
            variant='contained'
            disabled={editDeviceLoading || !editingDevice}
          >
            {editDeviceLoading && editingDevice ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default SeniorDetail;
// import { useParams } from "react-router-dom";

// export default function SeniorDetail() {
//   const { senior_id } = useParams();
//   console.log("senior_id:", senior_id);

//   return <h1>Senior ID: {senior_id}</h1>;
// }
